import { createApp } from 'vue'
import App from './App.vue'
import {createPinia} from 'pinia'
import router from './router'

var moment = require('moment');
moment.locale('es_ES')

/*
|--------------------------------------------------------------------------
| 2. Axios for the Win & Glory
|--------------------------------------------------------------------------
|
| Todas las llamadas a la app de brainspro serán realizadas con axios.
| Será mejor que lo importemos si no pocas llamadas podrán hacerse.
| Absténgase de usar el cliente http de vue-resource si lo tienes en mente
| los tipos que usan vue-resource no nos gustan ¬¬
|
| La api responderá siempre con JSON, así que mejor será decirlo en cada
| petición.
|
*/

import axios from 'axios'
import {useAuthUserStore} from "@/stores/auth";
axios.defaults.headers.common['Accept'] = 'application/json';

let isHandling401 = false
axios.interceptors.response.use(
    response => response,
    error => {
        const status = error?.response?.status
        if (status === 401 && !isHandling401) {
            isHandling401 = true
            //TODO PINIA AUTH
            return useAuthUserStore().logout({ revoke: false })
                .catch(() => {})
                .finally(() => {
                    isHandling401 = false
                    router.push('/login')
                })
        }

        return Promise.reject(error)
    }
)

// Cabecera del editor de lección: anterior/siguiente y «Ver como alumno» (FE-053).
// Va aparte del bloque de iconos para no chocar con otras PR que añaden iconos.
import { library as lessonHeaderIcons } from '@fortawesome/fontawesome-svg-core'
import { faChevronLeft as faLessonPrevious, faChevronRight as faLessonNext, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
lessonHeaderIcons.add(faLessonPrevious, faLessonNext, faExternalLinkAlt)

// Flecha de «Volver al contenido» del editor de lección (FE-056: solo Font Awesome en ProjectEdit/).
import { library as backLinkIcons } from '@fortawesome/fontawesome-svg-core'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
backLinkIcons.add(faArrowLeft)

// Aviso de que otra persona ha guardado la lección en el editor de bloques (FE-055).
import { library as lessonBlocksSaveIcons } from '@fortawesome/fontawesome-svg-core'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
lessonBlocksSaveIcons.add(faExclamationTriangle)

// Bloque de imagen del editor de lecciones (FE-057).
import { library as imageBlockIcons } from '@fortawesome/fontawesome-svg-core'
import { faImage } from '@fortawesome/free-solid-svg-icons'
imageBlockIcons.add(faImage)

// Bloque de archivos descargables del editor de lecciones (FE-058).
import { library as fileBlockIcons } from '@fortawesome/fontawesome-svg-core'
import { faFileAlt, faFileUpload, faRedo } from '@fortawesome/free-solid-svg-icons'
fileBlockIcons.add(faFileAlt, faFileUpload, faRedo)

/*
|--------------------------------------------------------------------------
| 3. Font Awesome
|--------------------------------------------------------------------------
|
| Usaremos estos iconos en toda la aplicación, quizás cuando se haga este
| código un poco monstruoso lo sacaremos a parte.
|
| No se usará más librerías que esta (se permiten iconos en formato svg).
|
*/

import { library } from '@fortawesome/fontawesome-svg-core'
import {
    faHeart,
    faGlobe,
    faCogs,
    faHome,
    faChartBar,
    faSchool,
    faChalkboardTeacher,
    faChalkboard,
    faGraduationCap,
    faBullhorn,
    faSpinner,
    faShoppingCart,
    faWallet,
    faSwatchbook,
    faIdCardAlt,
    faFileContract,
    faLanguage,
    faCalendarDay,
    faUserGraduate,
    faUsers, faBan, faBolt, faPiggyBank,
    faFileCode, faTag, faListUl, faInfoCircle, faMapMarkerAlt, faLaptop,
    faCertificate, faEnvelope, faCode, faLock,
    faTicketAlt, faCopy, faCheck, faClock, faPause, faUndo, faTimes, faTrash, faFileExcel,
    faArrowUp, faArrowDown, faThLarge, faChevronDown,
    faPlus, faGripLines, faChevronUp, faChevronRight, faAngleDoubleUp, faAngleDoubleDown, faEllipsisH,
    faAlignLeft, faVideo, faQuestionCircle
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
// Menú «⋯» y añadir lección del currículo (FE-061)
import { faEllipsisH as faCurriculumEllipsis, faPlus as faCurriculumPlus } from '@fortawesome/free-solid-svg-icons'
// Filas de lección del currículo (FE-063)
import { faGripVertical, faPen, faEye, faEyeSlash, faUnlock } from '@fortawesome/free-solid-svg-icons'

library.add(
    faBolt, // Utilizado para las integraciones en la pestaña marketing
    faGlobe, // Mundo para configuración de dominio
    faSwatchbook, // Paleta de colores para zona de personalización de academia
    faIdCardAlt, // Tarjeta identificación para contacto y rrss de academia
    faLanguage, // Icono para la pestaña de idioma de la academia
    faCalendarDay, // Icono para la tarjeta de ventas del mes en el dashboard
    faUserGraduate, // Avatar del asistente en el widget de la base de conocimiento del dashboard
    faBan, // Para bloquear permisos o si no se tiene un funcionalidad en concreto
    faCogs,
    faHome,
    faChartBar,
    faHeart, //Para made with love in málaga
    faPiggyBank, // Para suscripciones en sales
    faUsers, // Icono de usuarios para el menú
    faFileContract, // Icono para zona de textos legales en la academia
    faFileCode, // Botón de alternar a modo HTML fuente en el editor TipTap
    faTag, // Botón de insertar aviso destacado en el editor TipTap
    faListUl, // Botón de insertar lista con iconos en el editor TipTap
    faInfoCircle, faMapMarkerAlt, faLaptop, faCertificate, faEnvelope, // Set de iconos seleccionables en la lista con iconos
    faCode, // Botón de insertar bloque de HTML personalizado (sin restricciones) en el editor TipTap
    faLock,
    faSchool, faChalkboardTeacher, faChalkboard, faGraduationCap, faBullhorn, faSpinner, faShoppingCart, faWallet,
    faTicketAlt, // Icono de cupón aplicado en la tabla de pedidos de Ventas
    faCopy, faCheck, faClock, faPause, faUndo, faTimes, faTrash, // Iconos de copiar ID y estado del pedido en Ventas
    faFileExcel, // Icono del botón de exportar pedidos a Excel
    faArrowUp, faArrowDown, // Iconos de variación vs. periodo anterior en las tarjetas de Ventas
    faThLarge, faChevronDown, // Menú desplegable "Bloques" del editor TipTap (aside del producto)
    faPlus, faGripLines, faChevronUp, faChevronRight, faAngleDoubleUp, faAngleDoubleDown, faEllipsisH, // Editor de bloques de lección (FE-052)
    faAlignLeft, faVideo, faQuestionCircle // Iconos de los tipos de bloque en el menú «Añadir bloque» (FE-052)
)

library.add(faCurriculumEllipsis, faCurriculumPlus)
library.add(faGripVertical, faPen, faEye, faEyeSlash, faUnlock)

const app = createApp(App)

import i18n_messages from '@/lang/messages';
import { createI18n } from 'vue-i18n'
import './assets/tailwind.css'
import VCalendar from 'v-calendar'
import 'v-calendar/dist/style.css';

const i18n = createI18n({
    locale: 'es',
    fallbackLocale: 'es',
    messages: i18n_messages
})

app.use(i18n)
app.use(createPinia())
app.use(router)
app.use(VCalendar, {})
app.component('font-awesome-icon', FontAwesomeIcon)

app.config.productionTip = false

app.mount('#app')

app.config.globalProperties.$moment = moment;

process.env.VUE_APP_VERSION = require('@/../package.json').version
